import { FishingReport, SeasonGroup, TechniqueFull, WaterbodyDetail } from '@omniafishing/core';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatToIso8601, formatToIsoDateZ, getMostRecent } from '../../lib/time';
import { getGenericMetaDescription, sqKmToAcres } from '../../lib/waterbody';
import { getAmbassadors } from '../../redux/brands';
import { getBaseUrl, getFullUrl } from '../../redux/router';
import { RoutePaths } from '../../routes';
import { MapQueryParams } from '../map_default/map_default';
import { waterbodyCountiesStatesString } from '../search_universal/search_waterbody';
import { SEO } from '../seo/seo';
import { waterbodyImgUrl } from '../waterbody_img/waterbody_img';
import { FIRST_PUBLISHED_DATE } from './waterbody_detail';
import { waterClarity } from './waterbody_detail_sections/waterbody_detail_learn_more/waterbody_detail_learn_more';

interface WaterbodyDetailSEOProps {
  fishingReports: FishingReport[];
  mostRecentFishingReportDate: dayjs.Dayjs | null;
  selectedSeasonGroup: SeasonGroup;
  waterbody: WaterbodyDetail;
  waterbodyFishingReportTechniquesWithCountsSorted: TechniqueFull[];
}
export const WaterbodyDetailSEO = React.memo((props: WaterbodyDetailSEOProps) => {
  const {
    fishingReports,
    selectedSeasonGroup,
    mostRecentFishingReportDate,
    waterbody,
    waterbodyFishingReportTechniquesWithCountsSorted,
  } = props;
  const {
    area_sqkm,
    average_depth_ft,
    classification,
    lat,
    lng,
    locales,
    meta_description,
    meta_title,
    omnia_clarity,
    primary_name,
    seo_indexed,
    species_known,
    species,
    water_temperature_recorded_at,
  } = waterbody;

  const ambassadors = useSelector(getAmbassadors);
  const selectedSeasonNames = selectedSeasonGroup.seasons.map((s) => s.name);
  const selectedSeasonSpeciesInReports = useMemo(() => {
    return fishingReports
      .filter((report) => selectedSeasonNames.includes(report.season.name))
      .map((report) => report.species);
  }, [selectedSeasonNames, fishingReports.map((r) => r.id).join('-')]);

  const seasonSpeciesDisplayNamesInReports = _.uniq(
    selectedSeasonSpeciesInReports.map((s) => s.display_name)
  ).join(', ');

  const baseUrl = useSelector(getBaseUrl);
  const mapRoute = `${baseUrl}${RoutePaths.MAP}?${MapQueryParams.lat}=${lat}&${
    MapQueryParams.lng
  }=${lng}&${MapQueryParams.zoom}=${10}`;
  const waterbodyUrl = useSelector(getFullUrl);

  const lastUpdatedWaterTempDate = water_temperature_recorded_at
    ? dayjs(water_temperature_recorded_at).startOf('day')
    : null;

  const waterbodyImg = waterbodyImgUrl(waterbody, {
    imgSize: { width: 125, height: 125, padding: 10 },
    hires: false,
  });

  const topThreeTechniqueNames = waterbodyFishingReportTechniquesWithCountsSorted
    .slice(0, 3)
    .map((t) => t.display_name)
    .join(', ');

  const waterbodyLocation = waterbodyCountiesStatesString(
    waterbody.locales.map((l) => l.state.abbr),
    _.flatten(waterbody.locales.map((l) => l.counties))
  );

  let waterbodyNameWithLocation = primary_name;

  if (waterbodyLocation) {
    waterbodyNameWithLocation += ` - ${waterbodyLocation}`;
  }

  const amenityFeature = [
    {
      '@type': 'LocationFeatureSpecification',
      name: 'Popular Techniques',
      value: topThreeTechniqueNames,
    },
  ];

  const knownSpeciesNames = species_known
    ? species
        .slice(0, 6)
        .map((s) => s.display_name)
        .join(', ')
    : '';
  const speciesNamesLdJson = seasonSpeciesDisplayNamesInReports || knownSpeciesNames;

  if (speciesNamesLdJson) {
    amenityFeature.push({
      '@type': 'LocationFeatureSpecification',
      name: 'Popular Species',
      value: speciesNamesLdJson,
    });
  }

  const waterbodyStateNames = locales.map((locale) => `${locale.state.name}`).join(', ');
  const waterbodyCountyNames = _.flatten(locales.map((locale) => locale.counties)).join(', ');

  const keywords = `Fishing, ${primary_name}, ${waterbodyStateNames}, ${waterbodyCountyNames}, ${topThreeTechniqueNames} ${speciesNamesLdJson}`;

  const getFAQDescription = (w: WaterbodyDetail) => {
    const speciesPresent = knownSpeciesNames || 'a variety of species';
    const currentSeasons = w.seasons
      .filter((season) => season.active)
      .map((season) => season.display_name)
      .join('/');
    const localesString = w.locales.map((locale) => `${locale.state.abbr}`).join(', ');
    return `${primary_name} is a body of water in ${localesString}. During this time of year, fish can be found in ${currentSeasons} patterns. Available species are ${speciesPresent}. Narrow down your tackle selection by adjusting season, water clarity, structure and technique.`;
  };

  const faqMainEntity = [
    {
      '@type': 'Question',
      name: `What do you need to know about ${primary_name}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: getFAQDescription(waterbody),
      },
    },
  ];

  const bodyOfWaterLdJsonType: Record<string, any> = {
    '@type': 'LakeBodyOfWater',
    name: waterbodyNameWithLocation,
    description: meta_description || getGenericMetaDescription(waterbody),
    url: waterbodyUrl,
    dateCreated: formatToIsoDateZ(FIRST_PUBLISHED_DATE),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lng,
    },
    image: waterbodyImg,
    publicAccess: true,
    amenityFeature,
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Top Reported Techniques',
        value: topThreeTechniqueNames,
      },
    ],
    keywords,
  };

  const lastUpdatedDate = getMostRecent(mostRecentFishingReportDate, lastUpdatedWaterTempDate);

  if (lastUpdatedDate) {
    bodyOfWaterLdJsonType.dateModified = formatToIso8601(lastUpdatedDate);
  }

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Place',
        geo: {
          '@type': 'GeoCoordinates',
          latitude: lat,
          longitude: lng,
        },
        url: waterbodyUrl,
        hasMap: mapRoute,
        name: waterbodyNameWithLocation,
        alternateName: meta_title,
        image: waterbodyImg,
        description: meta_description || getGenericMetaDescription(waterbody),
      },
      bodyOfWaterLdJsonType,
      {
        '@type': 'FAQPage',
        mainEntity: faqMainEntity,
      },
    ],
  };

  const averageDepth = Math.round(average_depth_ft);

  if (area_sqkm || averageDepth || omnia_clarity) {
    const lakeAreaString = `${sqKmToAcres(area_sqkm)} acres`;
    const averageDepthString = `an average depth of ${averageDepth}ft. and`;
    const waterClarityString = `${waterClarity(omnia_clarity)
      .split(' ')[0]
      .toLowerCase()} water clarity.`;
    const lakeDetailsFAQ = {
      '@type': 'Question',
      name: `What is ${primary_name}'s size, depth, and clarity?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${primary_name} is a ${area_sqkm && lakeAreaString} ${
          classification && classification.name
        } with ${averageDepth && averageDepthString} a ${omnia_clarity && waterClarityString}`,
      },
    };

    faqMainEntity.push(lakeDetailsFAQ);
  }

  if (species.length) {
    const speciesList = species.map((s) => s.display_name).join(', ');
    const speciesFAQ = {
      '@type': 'Question',
      name: `What fish are in ${primary_name}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${primary_name} contains the following species of fish: ${speciesList}.`,
      },
    };

    faqMainEntity.push(speciesFAQ);
  }

  if (ambassadors.length) {
    const ambassadorList = ambassadors
      .slice(0, 5)
      .map((a) => a.full_name)
      .join(', ');

    const ambassadorsFAQ = {
      '@type': 'Question',
      name: `Who are the top ambassadors for ${primary_name}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `The top ambassadors on ${primary_name} are ${ambassadorList}`,
      },
    };
    faqMainEntity.push(ambassadorsFAQ);
  }
  return (
    <SEO
      title={meta_title || primary_name}
      description={meta_description || getGenericMetaDescription(waterbody)}
      meta={seo_indexed ? [] : [{ name: 'robots', content: 'noindex, follow' }]}
      schema={schema}
    />
  );
});
